import { Fragment, useState } from 'react';
import type { CollectorRunStatus, DeveloperHealth, DeveloperHealthState, DeveloperRunHistoryEntry } from '@ace-tracker/shared';
import { SOURCE_LABELS, timeAgo, useDeveloperHealth, useDeveloperRunHistory } from '../health';
import { DeveloperName, useDeveloperProfiles } from '../settings';
import { ExportMenu } from './ExportMenu';
import { InfoTooltip } from './InfoTooltip';
import type { SectionHeadingProps } from './OptimizationShared';

const DASH = '—';

const DEV_STATE: Record<DeveloperHealthState, { label: string; icon: string; chip: string; meaning: string }> = {
  healthy: { label: 'Healthy', icon: 'fa-solid fa-circle-check', chip: 'bg-ok-bg text-ok-ink', meaning: 'The collector ran recently and at least one active source succeeded. Skipped sources (not installed, switched off, nothing new) are ignored.' },
  failing: { label: 'Failing', icon: 'fa-solid fa-triangle-exclamation', chip: 'bg-crit-bg text-crit-ink', meaning: 'No active source succeeded and at least one failed.' },
  stale: { label: 'Stale', icon: 'fa-solid fa-clock-rotate-left', chip: 'bg-warn-bg text-warn-ink', meaning: 'Nothing has arrived from this developer within the stale threshold (Settings → Data health).' },
  'no-run-reports': { label: 'Older collector', icon: 'fa-solid fa-circle-question', chip: 'bg-warn-bg text-warn-ink', meaning: 'Telemetry arrives, but the machine sends no run reports. It needs collector 1.4.1 or later.' },
};

const RUN_STATUS: Record<CollectorRunStatus, { label: string; icon: string; chip: string }> = {
  ok: { label: 'OK', icon: 'fa-solid fa-circle-check', chip: 'bg-ok-bg text-ok-ink' },
  partial: { label: 'Partial', icon: 'fa-solid fa-circle-half-stroke', chip: 'bg-warn-bg text-warn-ink' },
  failed: { label: 'Failed', icon: 'fa-solid fa-triangle-exclamation', chip: 'bg-crit-bg text-crit-ink' },
  skipped: { label: 'Skipped', icon: 'fa-solid fa-forward', chip: 'bg-surface-alt text-text-muted' },
};

const SKIP_REASON: Record<string, string> = {
  disabled: 'switched off',
  'not-installed': 'not installed',
  'no-data': 'nothing new',
  unchanged: 'unchanged',
  'identity-unavailable': 'identity unavailable',
  'not-allowed': 'not allowed',
  'invalid-source': 'invalid source',
  'dry-run': 'dry run',
  error: 'error',
  other: 'other',
};

function When({ iso }: { iso: string | null }) {
  if (!iso) return <span className="text-text-faint">{DASH}</span>;
  return <span title={new Date(iso).toLocaleString()}>{timeAgo(iso)}</span>;
}

function Chip({ chip, icon, label, title }: { chip: string; icon: string; label: string; title?: string }) {
  return (
    <span title={title} className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-pill px-2.5 py-0.5 text-xs font-medium ${chip}`}>
      <i aria-hidden="true" className={icon} />
      {label}
    </span>
  );
}

function StatusChip({ status, skipReason }: { status: CollectorRunStatus; skipReason: string | null }) {
  const style = RUN_STATUS[status];
  const reason = skipReason ? SKIP_REASON[skipReason] ?? skipReason : null;
  return <Chip chip={style.chip} icon={style.icon} label={reason && status !== 'ok' ? `${style.label} · ${reason}` : style.label} />;
}

function count(value: number | null): string {
  return value === null ? DASH : value.toLocaleString();
}

function duration(ms: number | null): string {
  if (ms === null) return DASH;
  return ms < 1000 ? `${Math.round(ms)} ms` : `${(ms / 1000).toFixed(1)} s`;
}

function RunHistory({ developerId, range }: { developerId: string; range: { from: string; to: string } }) {
  const { data, isLoading, isError, error } = useDeveloperRunHistory(developerId, range, true);
  if (isLoading) return <p className="text-sm text-text-muted">Loading run history…</p>;
  if (isError || !data) return <p className="text-sm font-medium text-error">Failed to load run history: {(error as Error | null)?.message ?? 'unknown error'}</p>;
  if (data.runs.length === 0) {
    return <p className="text-sm text-text-muted">No collector runs from this developer in the selected range. Pick a longer range to look further back.</p>;
  }
  return (
    <div>
      <div className="max-h-96 overflow-auto">
        <table className="w-full min-w-[760px] text-left text-sm">
          <thead className="sticky top-0 border-b border-border bg-surface-alt text-[11px] uppercase tracking-wide text-text-faint">
            <tr>
              <th scope="col" className="pb-2 pr-3">Time</th>
              <th scope="col" className="pb-2 pr-3">Source</th>
              <th scope="col" className="pb-2 pr-3">Outcome</th>
              <th scope="col" className="pb-2 pr-3 text-right">Sent</th>
              <th scope="col" className="pb-2 pr-3 text-right">Accepted</th>
              <th scope="col" className="pb-2 pr-3 text-right">Rejected</th>
              <th scope="col" className="pb-2 pr-3 text-right">Duration</th>
              <th scope="col" className="pb-2">Version</th>
            </tr>
          </thead>
          <tbody>
            {data.runs.map((run: DeveloperRunHistoryEntry, i) => (
              <tr key={`${run.at}-${run.source}-${i}`} className="border-b border-border last:border-0">
                <td className="py-2 pr-3 tabular-nums" title={run.device ? `Device ${run.device}` : undefined}>{new Date(run.at).toLocaleString()}</td>
                <td className="py-2 pr-3">{SOURCE_LABELS[run.source]}</td>
                <td className="py-2 pr-3"><StatusChip status={run.status} skipReason={run.skipReason} /></td>
                <td className="py-2 pr-3 text-right tabular-nums">{count(run.itemsSent)}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{count(run.itemsAccepted)}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{count(run.itemsRejected)}</td>
                <td className="py-2 pr-3 text-right tabular-nums text-text-muted">{duration(run.durationMs)}</td>
                <td className="py-2 font-mono text-xs text-text-muted">{run.collectorVersion ?? DASH}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-xs text-text-faint">
        {data.runs.length} {data.runs.length === 1 ? 'run' : 'runs'}, newest first{data.truncated ? ' — showing the newest only; narrow the range to see older ones' : ''}. A dash means not reported.
      </p>
    </div>
  );
}

function SourceBreakdown({ developer }: { developer: DeveloperHealth }) {
  if (developer.sources.length === 0) {
    return <p className="text-sm text-text-muted">This machine sends no collector run reports, so per-source status is unknown. Update it to collector 1.4.1 or later.</p>;
  }
  return (
    <ul className="flex flex-wrap gap-2.5">
      {developer.sources.map((s) => (
        <li key={s.source} className="rounded-card border border-border bg-surface px-3 py-2 text-xs">
          <p className="mb-1 font-medium text-text-default">{SOURCE_LABELS[s.source]}</p>
          <StatusChip status={s.status} skipReason={s.skipReason} />
          <p className="mt-1.5 text-text-muted">Last run <When iso={s.lastRun} /></p>
          <p className="text-text-muted">Last sent <When iso={s.lastSuccessfulSend} /></p>
        </li>
      ))}
    </ul>
  );
}

/**
 * Developer-level view of the pipeline: when each developer's machine last ran
 * the collector and last got data through, and every run in the selected range
 * when a row is opened. Rows are measured back from now; the history follows
 * the selected range. Pipeline facts only — no usage figures.
 */
export function DeveloperHealthSection({ range, SectionHeading }: { range: { from: string; to: string }; SectionHeading: (props: SectionHeadingProps) => JSX.Element }) {
  const { data, isLoading, isError, error } = useDeveloperHealth();
  const { label } = useDeveloperProfiles();
  const [open, setOpen] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'attention'>('all');

  const developers = data?.developers ?? [];
  const attention = developers.filter((d) => d.state !== 'healthy');
  const shown = filter === 'attention' ? attention : developers;

  return (
    <section>
      <SectionHeading
        title="Developers"
        hint={data ? `Runs from the last ${data.basisDays} days · stale after ${data.staleAfterHours} h` : 'When each developer’s collector last ran'}
      />
      <div data-export-root className="rounded-card border border-border bg-surface p-5 shadow-sm">
        {isLoading && <p className="text-sm text-text-muted">Loading developers…</p>}
        {isError && <p className="text-sm font-medium text-error">Failed to load developer health: {(error as Error).message}</p>}
        {data && (
          <>
            <div className="mb-3 flex flex-wrap items-center gap-3 text-sm">
              <p className="mr-auto text-text-muted">
                {developers.length} {developers.length === 1 ? 'developer' : 'developers'}
                {attention.length > 0 ? `, ${attention.length} needing attention` : ', all healthy'}
              </p>
              <ExportMenu title="Developer health" />
              <div role="group" aria-label="Filter developers" className="inline-flex overflow-hidden rounded-card border border-border">
                {(['all', 'attention'] as const).map((f) => (
                  <button
                    key={f}
                    type="button"
                    aria-pressed={filter === f}
                    onClick={() => setFilter(f)}
                    className={`min-h-[44px] px-3 text-xs font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-blue ${filter === f ? 'bg-primary-blue text-white' : 'bg-surface text-text-muted hover:text-text-default'}`}
                  >
                    {f === 'all' ? 'All' : 'Needs attention'}
                  </button>
                ))}
              </div>
            </div>

            {shown.length === 0 ? (
              <p className="text-sm text-text-muted">{developers.length === 0 ? `No developer sent telemetry in the last ${data.basisDays} days.` : 'Every developer is healthy.'}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[960px] text-left text-sm">
                  <thead className="border-b border-border text-[11px] uppercase tracking-wide text-text-faint">
                    <tr>
                      <th scope="col" className="pb-2.5 pr-3">Developer</th>
                      <th scope="col" className="pb-2.5 pr-3">Status</th>
                      <th scope="col" className="pb-2.5 pr-3 text-right">
                        <span className="inline-flex items-center gap-1">Last collector run <InfoTooltip text="When the collector on this developer's machine last executed, for any source. Needs collector 1.4.1 or later." /></span>
                      </th>
                      <th scope="col" className="pb-2.5 pr-3 text-right">
                        <span className="inline-flex items-center gap-1">Last data sent <InfoTooltip text="When a run last got data through to ingestion, as reported by the collector." /></span>
                      </th>
                      <th scope="col" className="pb-2.5 pr-3 text-right">
                        <span className="inline-flex items-center gap-1">Last telemetry <InfoTooltip text="Latest usage telemetry from this developer that reached the workspace." /></span>
                      </th>
                      <th scope="col" className="pb-2.5 pr-3 text-right">Devices</th>
                      <th scope="col" className="pb-2.5 pr-3">Version</th>
                      <th scope="col" className="pb-2.5 text-right">
                        <span className="inline-flex items-center gap-1">Runs <InfoTooltip text="Collector runs in the look-back window, and how many of them failed." /></span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((d) => {
                      const expanded = open === d.developerId;
                      const style = DEV_STATE[d.state];
                      const panelId = `dev-health-${d.developerId}`;
                      return (
                        <Fragment key={d.developerId}>
                          <tr className="border-b border-border last:border-0">
                            <th scope="row" className="py-1.5 pr-3 font-normal">
                              <button
                                type="button"
                                aria-expanded={expanded}
                                aria-controls={panelId}
                                aria-label={`${expanded ? 'Hide' : 'Show'} run history for ${label(d.developerId)}`}
                                onClick={() => setOpen(expanded ? null : d.developerId)}
                                className="flex min-h-[44px] items-center gap-2 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-blue"
                              >
                                <i aria-hidden="true" className={`fa-solid ${expanded ? 'fa-chevron-down' : 'fa-chevron-right'} w-3 text-xs text-text-faint`} />
                                <DeveloperName developerId={d.developerId} showTeam />
                              </button>
                            </th>
                            <td className="py-1.5 pr-3">
                              <div className="flex flex-wrap items-center gap-1.5">
                                <Chip chip={style.chip} icon={style.icon} label={style.label} title={style.meaning} />
                                {d.olderCollector && d.state !== 'no-run-reports' && (
                                  <Chip chip="bg-warn-bg text-warn-ink" icon="fa-solid fa-circle-question" label="Older collector" title="At least one machine sends no run reports and needs collector 1.4.1 or later. Informational — it does not affect the status." />
                                )}
                              </div>
                            </td>
                            <td className="py-1.5 pr-3 text-right tabular-nums"><When iso={d.lastRun} /></td>
                            <td className="py-1.5 pr-3 text-right tabular-nums text-text-muted"><When iso={d.lastSuccessfulSend} /></td>
                            <td className="py-1.5 pr-3 text-right tabular-nums text-text-muted"><When iso={d.lastTelemetry} /></td>
                            <td className="py-1.5 pr-3 text-right tabular-nums">{d.devices}</td>
                            <td className="py-1.5 pr-3 font-mono text-xs text-text-muted">{d.collectorVersion ?? DASH}</td>
                            <td className="py-1.5 text-right tabular-nums" title={`${d.runs} runs, ${d.failedRuns} failed, ${d.partialRuns} partial`}>
                              {d.runs > 0 ? `${d.runs}${d.failedRuns > 0 ? ` (${d.failedRuns} failed)` : ''}` : DASH}
                            </td>
                          </tr>
                          {expanded && (
                            <tr id={panelId} className="border-b border-border last:border-0">
                              <td colSpan={8} className="bg-surface-alt px-4 py-4">
                                <div className="space-y-4">
                                  <SourceBreakdown developer={d} />
                                  <div>
                                    <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-text-faint">Run history for the selected range</h4>
                                    <RunHistory developerId={d.developerId} range={range} />
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <ul className="mt-4 grid grid-cols-1 gap-2 border-t border-border pt-3 text-xs text-text-muted sm:grid-cols-2">
              {(Object.keys(DEV_STATE) as DeveloperHealthState[]).map((state) => (
                <li key={state} className="flex items-start gap-2">
                  <Chip chip={DEV_STATE[state].chip} icon={DEV_STATE[state].icon} label={DEV_STATE[state].label} />
                  <span>{DEV_STATE[state].meaning}</span>
                </li>
              ))}
            </ul>
            <p className="mt-3 text-xs text-text-faint">
              Developers are those who sent telemetry in the last {data.basisDays} days — a machine where the collector never ran cannot appear. Machines are identified by hashed name; a dash means not known, never zero.
            </p>
          </>
        )}
      </div>
    </section>
  );
}
